<template>
  <div
    class="w-[29rem] flex flex-col gap-2 mb-4 p-2 bg-neutral-secondary rounded-md"
  >
    <NText variant="caption1" weight="bold">
      {{ t('modules.order.microTransactions') }}
    </NText>

    <div class="bg-neutral-primary rounded-md">
      <div
        class="flex justify-between items-center py-2 px-2 border-b border-neutral-secondary"
      >
        <NText variant="caption2" weight="medium">
          {{ t('modules.order.AverageTransactionPrice') }}
        </NText>

        <span>
          <NumberFormatter
            #="{ number }"
            :value="
              omsAverageTransactionPrice(
                microTransactionsValueNoWage,
                microTransactionsQuantities
              )
            "
            round
          >
            <NText variant="caption2" weight="medium" class="pe-1">
              {{ number }}
            </NText>
          </NumberFormatter>
          <NText
            variant="overline"
            weight="regular"
            class="text-neutral-secondary"
          >
            {{ t('misc.rial') }}
          </NText>
        </span>
      </div>

      <div class="flex justify-between items-center py-2 px-2">
        <NText variant="caption2" weight="medium">
          {{ t('modules.order.microTransactionsValue') }}
        </NText>

        <span>
          <NumberFormatter #="{ number }" :value="microTransactionsValue" round>
            <NText variant="caption2" weight="medium" class="pe-1">
              {{ number }}
            </NText>
          </NumberFormatter>
          <NText
            variant="overline"
            weight="regular"
            class="text-neutral-secondary"
          >
            {{ t('misc.rial') }}
          </NText>
        </span>
      </div>
    </div>

    <div class="bg-neutral-secondary rounded-md">
      <MicroTransactionTable :trades="microTransactionsList" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  useGetMicroTransactionsQuery,
  omsAverageTransactionPrice,
} from '@core/order';
import { OrderItem } from '@libs/api-reference';
import { computed } from 'vue';

import NumberFormatter from '@/components/Number.vue';

import MicroTransactionTable from './MicroTransactionTable.vue';

type MicroTransactionProps = {
  order: OrderItem;
  isLegacy: boolean;
};

const props = defineProps<MicroTransactionProps>();

const { t } = useI18n();

const { microTransactionsList } = useGetMicroTransactionsQuery(
  props.order.orderId,
  props.isLegacy
);

const microTransactionsValue = computed(() => {
  return (
    props.order?.executedValue ||
    microTransactionsList.value.reduce<number>(
      (previousValue, currentValue) => {
        if (currentValue.value) {
          return previousValue + currentValue?.value;
        }

        return previousValue;
      },
      0
    )
  );
});

const microTransactionsValueNoWage = computed(() => {
  return microTransactionsList.value.reduce<number>(
    (previousValue, currentValue) => {
      if (currentValue.price && currentValue.quantity) {
        return previousValue + currentValue.price * currentValue.quantity;
      }

      return previousValue;
    },
    0
  );
});

const microTransactionsQuantities = computed(() => {
  return microTransactionsList.value.reduce<number>(
    (previousValue, currentValue) => {
      if (currentValue.quantity) {
        return previousValue + currentValue?.quantity;
      }

      return previousValue;
    },
    0
  );
});
</script>
